import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Building2, MapPin, Tag, Save, Plus, Trash2, Edit2, CheckCircle2 } from 'lucide-react';
import { cn } from '../lib/utils';
type Tab = 'business' | 'categories' | 'locations';
interface Location {
  id: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  active: boolean;
}
export function Settings() {
  const [activeTab, setActiveTab] = useState<Tab>('business');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success'>('idle');
  // Mock Data State
  const [businessInfo, setBusinessInfo] = useState({
    name: 'Acme Solar Solutions',
    description: 'Leading provider of residential solar installations and maintenance services.',
    website: 'https://acmesolar.com',
    email: 'contact@acmesolar.com',
    phone: '(555) 123-4567',
    taxId: '12-3456789',
    type: 'corporate',
    logoUrl: 'https://via.placeholder.com/150'
  });
  const [categories, setCategories] = useState({
    primary: 'Services',
    secondary: ['Automotive', 'Healthcare']
  });
  const [locations, setLocations] = useState<Location[]>([{
    id: '1',
    address: '123 Main St',
    city: 'New York',
    state: 'NY',
    zip: '10001',
    active: true
  }, {
    id: '2',
    address: '456 Oak Ave',
    city: 'Brooklyn',
    state: 'NY',
    zip: '11201',
    active: true
  }, {
    id: '3',
    address: '789 Elm St',
    city: 'Queens',
    state: 'NY',
    zip: '11354',
    active: false
  }]);
  // Location Editing State
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [newLocation, setNewLocation] = useState<Partial<Location>>({});
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const handleSave = () => {
    setSaveStatus('saving');
    // Simulate API call
    setTimeout(() => {
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }, 1000);
  };
  const handleAddLocation = () => {
    if (newLocation.address && newLocation.city) {
      const location: Location = {
        id: Math.random().toString(36).substr(2, 9),
        address: newLocation.address || '',
        city: newLocation.city || '',
        state: newLocation.state || '',
        zip: newLocation.zip || '',
        active: true
      };
      setLocations([...locations, location]);
      setNewLocation({});
      setIsAddingLocation(false);
      handleSave();
    }
  };
  const handleUpdateLocation = (id: string, updates: Partial<Location>) => {
    setLocations(locations.map(loc => loc.id === id ? {
      ...loc,
      ...updates
    } : loc));
  };
  const handleDeleteLocation = (id: string) => {
    setLocations(locations.filter(loc => loc.id !== id));
    handleSave();
  };
  const CATEGORY_OPTIONS = [{
    value: 'retail',
    label: 'Retail'
  }, {
    value: 'restaurant',
    label: 'Restaurant'
  }, {
    value: 'services',
    label: 'Services'
  }, {
    value: 'healthcare',
    label: 'Healthcare'
  }, {
    value: 'automotive',
    label: 'Automotive'
  }, {
    value: 'beauty',
    label: 'Beauty'
  }, {
    value: 'fitness',
    label: 'Fitness'
  }, {
    value: 'entertainment',
    label: 'Entertainment'
  }, {
    value: 'other',
    label: 'Other'
  }];
  return <div className="space-y-8 max-w-4xl mx-auto" data-id="element-565">
      <div data-id="element-566">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900" data-id="element-567">
          Settings
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-568">
          Manage your business profile and preferences.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 rounded-xl bg-slate-100 p-1" data-id="element-569">
        <button onClick={() => setActiveTab('business')} className={cn('flex items-center gap-2 w-full rounded-lg py-2.5 text-sm font-medium leading-5 ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2', activeTab === 'business' ? 'bg-white text-[#218080] shadow' : 'text-slate-600 hover:bg-white/[0.12] hover:text-slate-800')} data-id="element-570">
          <Building2 className="w-4 h-4 ml-auto lg:ml-4" data-id="element-571" />
          <span className="mr-auto" data-id="element-572">Business Info</span>
        </button>
        <button onClick={() => setActiveTab('categories')} className={cn('flex items-center gap-2 w-full rounded-lg py-2.5 text-sm font-medium leading-5 ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2', activeTab === 'categories' ? 'bg-white text-[#218080] shadow' : 'text-slate-600 hover:bg-white/[0.12] hover:text-slate-800')} data-id="element-573">
          <Tag className="w-4 h-4 ml-auto lg:ml-4" data-id="element-574" />
          <span className="mr-auto" data-id="element-575">Categories</span>
        </button>
        <button onClick={() => setActiveTab('locations')} className={cn('flex items-center gap-2 w-full rounded-lg py-2.5 text-sm font-medium leading-5 ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2', activeTab === 'locations' ? 'bg-white text-[#218080] shadow' : 'text-slate-600 hover:bg-white/[0.12] hover:text-slate-800')} data-id="element-576">
          <MapPin className="w-4 h-4 ml-auto lg:ml-4" data-id="element-577" />
          <span className="mr-auto" data-id="element-578">Locations</span>
        </button>
      </div>

      {/* Content */}
      <Card data-id="element-579">
        <CardContent className="p-6" data-id="element-580">
          {/* Business Info Tab */}
          {activeTab === 'business' && <div className="space-y-6" data-id="element-581">
              <div className="grid gap-6 md:grid-cols-2" data-id="element-582">
                <div className="space-y-4" data-id="element-583">
                  <Input label="Business Name" value={businessInfo.name} onChange={e => setBusinessInfo({
                ...businessInfo,
                name: e.target.value
              })} data-id="element-584" />
                  <Input label="Website" value={businessInfo.website} onChange={e => setBusinessInfo({
                ...businessInfo,
                website: e.target.value
              })} data-id="element-585" />
                  <Input label="Contact Email" type="email" value={businessInfo.email} onChange={e => setBusinessInfo({
                ...businessInfo,
                email: e.target.value
              })} data-id="element-586" />
                  <Input label="Contact Phone" type="tel" value={businessInfo.phone} onChange={e => setBusinessInfo({
                ...businessInfo,
                phone: e.target.value
              })} data-id="element-587" />
                </div>
                <div className="space-y-4" data-id="element-588">
                  <div className="space-y-2" data-id="element-589">
                    <label className="text-sm font-medium text-slate-700" data-id="element-590">
                      Description
                    </label>
                    <textarea className="flex min-h-[120px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#218080] focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50" value={businessInfo.description} onChange={e => setBusinessInfo({
                  ...businessInfo,
                  description: e.target.value
                })} data-id="element-591" />
                  </div>
                  <Input label="Tax ID" value={businessInfo.taxId} readOnly className="bg-slate-50 text-slate-500" data-id="element-592" />
                  <Select label="Business Type" value={businessInfo.type} onChange={e => setBusinessInfo({
                ...businessInfo,
                type: e.target.value
              })} options={[{
                value: 'solo',
                label: 'Solo Proprietorship'
              }, {
                value: 'franchise',
                label: 'Franchise'
              }, {
                value: 'corporate',
                label: 'Corporate'
              }]} data-id="element-593" />
                </div>
              </div>
            </div>}

          {/* Categories Tab */}
          {activeTab === 'categories' && <div className="space-y-6 max-w-2xl" data-id="element-594">
              <Select label="Primary Category" value={categories.primary.toLowerCase()} onChange={e => setCategories({
            ...categories,
            primary: e.target.value
          })} options={CATEGORY_OPTIONS} data-id="element-595" />

              <div className="space-y-3" data-id="element-596">
                <label className="text-sm font-medium text-slate-700" data-id="element-597">
                  Secondary Categories
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-598">
                  {CATEGORY_OPTIONS.map(cat => <label key={cat.value} className="flex items-center space-x-2 cursor-pointer" data-id="element-599">
                      <input type="checkbox" className="rounded border-slate-300 text-[#218080] focus:ring-[#218080]" checked={categories.secondary.map(c => c.toLowerCase()).includes(cat.value)} onChange={e => {
                  const newSecondary = e.target.checked ? [...categories.secondary, cat.label] : categories.secondary.filter(c => c !== cat.label);
                  setCategories({
                    ...categories,
                    secondary: newSecondary
                  });
                }} data-id="element-600" />
                      <span className="text-sm text-slate-600" data-id="element-601">
                        {cat.label}
                      </span>
                    </label>)}
                </div>
              </div>
            </div>}

          {/* Locations Tab */}
          {activeTab === 'locations' && <div className="space-y-6" data-id="element-602">
              <div className="flex justify-between items-center" data-id="element-603">
                <h3 className="text-lg font-medium text-slate-900" data-id="element-604">
                  Manage Locations
                </h3>
                <Button onClick={() => setIsAddingLocation(true)} size="sm" className="gap-2" data-id="element-605">
                  <Plus className="w-4 h-4" data-id="element-606" /> Add Location
                </Button>
              </div>

              {isAddingLocation && <Card className="bg-slate-50 border-slate-200" data-id="element-607">
                  <CardContent className="p-4 space-y-4" data-id="element-608">
                    <h4 className="font-medium text-slate-900" data-id="element-609">New Location</h4>
                    <div className="grid gap-4 md:grid-cols-2" data-id="element-610">
                      <Input placeholder="Address" value={newLocation.address || ''} onChange={e => setNewLocation({
                  ...newLocation,
                  address: e.target.value
                })} data-id="element-611" />
                      <div className="grid grid-cols-3 gap-2" data-id="element-612">
                        <Input placeholder="City" value={newLocation.city || ''} onChange={e => setNewLocation({
                    ...newLocation,
                    city: e.target.value
                  })} data-id="element-613" />
                        <Input placeholder="State" value={newLocation.state || ''} onChange={e => setNewLocation({
                    ...newLocation,
                    state: e.target.value
                  })} data-id="element-614" />
                        <Input placeholder="Zip" value={newLocation.zip || ''} onChange={e => setNewLocation({
                    ...newLocation,
                    zip: e.target.value
                  })} data-id="element-615" />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end" data-id="element-616">
                      <Button variant="ghost" size="sm" onClick={() => setIsAddingLocation(false)} data-id="element-617">
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleAddLocation} data-id="element-618">
                        Add Location
                      </Button>
                    </div>
                  </CardContent>
                </Card>}

              <div className="space-y-4" data-id="element-619">
                {locations.map(location => <div key={location.id} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-colors" data-id="element-620">
                    <div className="space-y-1" data-id="element-621">
                      <div className="flex items-center gap-2" data-id="element-622">
                        <p className="font-medium text-slate-900" data-id="element-623">
                          {location.address}
                        </p>
                        <Badge variant={location.active ? 'success' : 'secondary'} data-id="element-624">
                          {location.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500" data-id="element-625">
                        {location.city}, {location.state} {location.zip}
                      </p>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-626">
                      <Button variant="ghost" size="sm" onClick={() => handleUpdateLocation(location.id, {
                  active: !location.active
                })} data-id="element-627">
                        {location.active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteLocation(location.id)} data-id="element-628">
                        <Trash2 className="w-4 h-4" data-id="element-629" />
                      </Button>
                    </div>
                  </div>)}
              </div>
            </div>}

          {/* Footer Actions */}
          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between" data-id="element-630">
            <div className="flex items-center gap-2 text-sm" data-id="element-631">
              {saveStatus === 'success' && <span className="text-green-600 flex items-center gap-1.5 animate-in fade-in slide-in-from-left-2" data-id="element-632">
                  <CheckCircle2 className="w-4 h-4" data-id="element-633" />
                  Settings updated successfully
                </span>}
            </div>
            <Button onClick={handleSave} disabled={saveStatus === 'saving'} className="min-w-[120px]" data-id="element-634">
              {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>;
}